/**
 * ConfirmRecallModal — confirm a lot recall (POST /inventory/lots/:id/recall/confirm, cap
 * inventory.recall.run). A motif (≥ 3 chars, médico-légal) is mandatory. The server quarantines the lot
 * and builds the biologiste worklist; it NEVER mutates result values. Re-confirming a quarantined lot is
 * an idempotent no-op (surfaced as an info toast).
 */
import { useEffect, useState, type FormEvent } from "react";
import { Button, FormField, Modal, Textarea, useToast } from "@/ui";
import { useCan } from "@/auth";
import { useT } from "@/app";
import { ApiError } from "@/api";
import { INV_CAPS } from "../caps";
import { useConfirmRecall, useLabId } from "../api";

export interface ConfirmRecallModalProps {
  open: boolean;
  lotId: string | null;
  reactifId?: string | null;
  affectedCount: number;
  onClose: () => void;
  onConfirmed: () => void;
}

export function ConfirmRecallModal({
  open,
  lotId,
  reactifId,
  affectedCount,
  onClose,
  onConfirmed,
}: ConfirmRecallModalProps) {
  const { t } = useT();
  const toast = useToast();
  const labId = useLabId();
  const canRecall = useCan(INV_CAPS.recallRun);
  const mutation = useConfirmRecall(labId);

  const [motif, setMotif] = useState("");
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    if (!open) return;
    setMotif("");
    setTouched(false);
  }, [open, lotId]);

  const motifError = touched && motif.trim().length < 3 ? t("inventory.field.required") : null;
  const valid = motif.trim().length >= 3;

  const handleClose = () => {
    if (mutation.isPending) return;
    onClose();
  };

  const submit = (e: FormEvent) => {
    e.preventDefault();
    setTouched(true);
    if (!valid || !canRecall || !lotId) return;
    mutation.mutate(
      { lotId, motif: motif.trim(), ...(reactifId ? { reactifId } : {}) },
      {
        onSuccess: (res) => {
          if (res.idempotent_noop) toast.info(t("inventory.recall.confirm.noop"));
          else toast.success(t("inventory.recall.confirm.success", { n: res.affected_count }));
          onConfirmed();
        },
        onError: (err) => toast.error(err instanceof ApiError ? err.message : t("inventory.field.required")),
      },
    );
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={t("inventory.recall.confirm.title")}
      footer={
        <>
          <Button variant="ghost" onClick={handleClose} disabled={mutation.isPending}>
            {t("inventory.action.cancel")}
          </Button>
          <Button
            type="submit"
            form="inventory-recall-confirm"
            variant="danger"
            loading={mutation.isPending}
            disabled={!canRecall || !valid}
          >
            {t("inventory.recall.confirm.submit")}
          </Button>
        </>
      }
    >
      <form id="inventory-recall-confirm" onSubmit={submit} noValidate>
        <div style={{ display: "flex", flexDirection: "column", gap: "var(--nv-s-4)" }}>
          <p style={{ margin: 0, color: "var(--nv-slate)" }}>{t("inventory.recall.confirm.warn")}</p>
          {affectedCount > 0 && (
            <p style={{ margin: 0, fontWeight: 600 }}>
              {t("inventory.recall.preview.count", { n: affectedCount })}
            </p>
          )}
          <FormField
            label={t("inventory.recall.motif")}
            hint={t("inventory.recall.motif.hint")}
            required
            {...(motifError ? { error: motifError } : {})}
          >
            {(p) => (
              <Textarea
                {...p}
                autoFocus
                rows={3}
                value={motif}
                invalid={!!motifError}
                onChange={(e) => setMotif(e.target.value)}
              />
            )}
          </FormField>
        </div>
      </form>
    </Modal>
  );
}
